import classNames from "classnames";
import { Cross } from "components/icons/Cross";
import { HTMLMotionProps, motion } from "framer-motion";
import { MouseEvent, ReactNode, useContext } from "react";
import { Button } from "../Button/Button";
import { FilterContext } from "../Filter/FilterContext";
import { stopPropagation } from "utils/stopPropagation";

type ChipProps = {
  children: ReactNode;
  icon?: ReactNode;
  isActive?: boolean;
  isRemovable?: boolean;
  isFixedFilter?: boolean;
  size?: "regular" | "small" | "xsmall";
  onRemove?: () => void;
  filterType?: string;
  preventOpenOnClick?: boolean;
  rounded?: boolean;
} & HTMLMotionProps<"button">;

export const Chip = ({
  children,
  isActive = false,
  size = "xsmall",
  isRemovable = true,
  isFixedFilter = false,
  icon,
  onRemove: onClose,
  filterType,
  preventOpenOnClick = false,
  rounded,
  ...props
}: ChipProps) => {
  const { setOpen, onSelect } = useContext(FilterContext);

  const onRemove = (e?: MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    onClose?.();
    setOpen(false);
  };

  const handleChipClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (!filterType) {
      onRemove();
    }

    if (isActive && filterType && !preventOpenOnClick) {
      onSelect?.(filterType);
      setOpen?.((open) => !open);
    }

    if (isFixedFilter && !isActive) {
      props.onClick?.(e);
    }
  };

  return (
    <motion.button
      layout
      key="chip"
      animate={{ opacity: 1, scale: 1 }}
      initial={{ opacity: 0, scale: 0.8 }}
      transition={{ duration: 0.2 }}
      {...props}
      onClick={handleChipClick}
      className={classNames(
        "all:unset t-flex t-gap-1 t-items-center t-text-text-60 t-border t-border-solid t-w-max t-select-none",
        {
          "t-h-6 t-text-caption": size === "small",
          "t-h-7 t-text-caption": size === "xsmall",
          "t-h-8 t-text-body-sm": size === "regular",
          "t-border-purple-40 t-bg-purple-0 has-[.close-button:hover]:!t-bg-red-10 has-[.close-button:hover]:!t-border-red-30":
            isActive,
          "t-border-surface-transparent t-bg-surface-grey":
            isFixedFilter && !isActive,
          "t-pl-3 t-pr-1.5": isActive && isRemovable,
          "t-px-3": !Boolean(isActive && isRemovable),
          "t-rounded-full": rounded,
          "t-rounded-md": !rounded,
        }
      )}
    >
      {icon}
      {children}
      {isActive && isRemovable && (
        <span className="close-button">
          <Button
            onClick={onRemove}
            onPointerDown={stopPropagation}
            customType="ghost_icon"
            size="xxsmall"
          >
            <Cross size="12" color="currentColor" />
          </Button>
        </span>
      )}
    </motion.button>
  );
};
